import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, 
  ArrowRight, 
  Send, 
  Loader2, 
  Check, 
  Calendar, 
  Clock, 
  User, 
  Stethoscope,
  FlaskConical,
  ClipboardList,
  Building2,
  PawPrint,
  CreditCard,
  FileText,
  ShoppingCart,
  DollarSign,
  AlertTriangle,
  Phone,
  Search,
  X,
  SlidersHorizontal,
  Folder,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* Normalize text for case- and accent-insensitive search */
const normalize = (s) =>
  (s || '')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Datos del Paciente', icon: PawPrint },
  { id: 2, title: 'Pruebas Solicitadas', icon: FlaskConical },
  { id: 3, title: 'Información Clínica', icon: ClipboardList },
  { id: 4, title: 'Resumen y Pago', icon: CreditCard },
];

// Section Header Component
const SectionHeader = ({ icon: Icon, title }) => (
  <div className="bg-[#505BB3] text-white px-4 py-3 rounded-t-lg flex items-center gap-2">
    {Icon && <Icon className="w-5 h-5" />}
    <h3 className="font-semibold">{title}</h3>
  </div>
);

// Card Container Component
const FormCard = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden ${className}`}>
    {children}
  </div>
);

// Category Indicator Component
const CategoryIndicator = ({ color }) => {
  const colorClasses = {
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    blue: 'bg-blue-500',
    orange: 'bg-orange-500',
    yellow: 'bg-yellow-500',
    pink: 'bg-pink-500',
  };
  
  return <span className={`w-2 h-2 rounded-full ${colorClasses[color]} inline-block mr-2`} />;
};

export default function ClinicaSolicitudForm() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [clinicaData, setClinicaData] = useState(null);
  const [formaCobro, setFormaCobro] = useState('cotizacion');
  const [gruposPermitidos, setGruposPermitidos] = useState([]);
  const [mostrarCultivos, setMostrarCultivos] = useState(true);
  const [veterinarios, setVeterinarios] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [pruebas, setPruebas] = useState([]);
  const [selectedPruebas, setSelectedPruebas] = useState([]);
  const [searchPrueba, setSearchPrueba] = useState('');
  const [especiesCatalogo, setEspeciesCatalogo] = useState([]);
  const [showOtraEspecie, setShowOtraEspecie] = useState(false);

  // Filters for Pruebas Solicitadas step
  const [areasDiagnosticas, setAreasDiagnosticas] = useState([]);
  const [aplicacionesClinicas, setAplicacionesClinicas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [appsPopoverOpen, setAppsPopoverOpen] = useState(false);
  const [appInternalSearch, setAppInternalSearch] = useState('');
  
  // Credit limit states
  const [showCreditLimitModal, setShowCreditLimitModal] = useState(false);
  const [creditLimitError, setCreditLimitError] = useState(null);

  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    hora_toma: new Date().toTimeString().slice(0, 5),
    veterinario_id: '',
    patient_info: {
      nombre: '',
      especie: '',
      raza: '',
      edad: '',
      sexo: '',
      propietario: '',
    },
    observaciones: '',
    clinical_data: {
      toma_antibioticos: false,
      fecha_ultima_dosis: null,
      alergico_antibioticos: false,
      cual_antibiotico: null,
      tiempo_evolucion: null,
      origen_toma_muestra: null,
    },
    cultivo_data: {
      faringea: [],
      exudado_diversos: [],
      nasal: [],
      urocultivo: { antecedente_infeccion: false, olor_fuerte: false, sintomas: [] },
      hongos_tipo_lesion: [],
      hongos_sitio: null,
      coprocultivo: [],
      coprocultivo_ingesta: null,
      traqueal: [],
    },
    sample_collection: {
      sangre: null,
      sangre_otra: null,
      orina: null,
      orina_otra: null,
      hisopados: null,
      hisopados_otra: null,
      observaciones_conservacion: null,
    },
  });

  const token = localStorage.getItem('clinica_token');

  useEffect(() => {
    if (!token) {
      navigate('/clinica/login');
      return;
    }
    
    const storedData = localStorage.getItem('clinica_data');
    if (storedData) {
      setClinicaData(JSON.parse(storedData));
    }
    
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const [vetsRes, gruposRes, pruebasRes, configRes, especiesRes, areasRes, appsRes] = await Promise.all([
        axios.get(`${API}/clinica/veterinarios`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/catalogo/grupos`, {
          headers: { 'Cache-Control': 'no-cache' }
        }),
        axios.get(`${API}/catalogo/pruebas`, {
          headers: { 'Cache-Control': 'no-cache' }
        }),
        axios.get(`${API}/clinica/configuracion`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/catalogo/especies`),
        axios.get(`${API}/catalogo/areas-diagnosticas`),
        axios.get(`${API}/catalogo/aplicaciones-clinicas`),
      ]);
      setVeterinarios(vetsRes.data);
      setGrupos(gruposRes.data);
      setPruebas(pruebasRes.data);
      setEspeciesCatalogo(especiesRes.data);
      setAreasDiagnosticas(areasRes.data);
      setAplicacionesClinicas(appsRes.data);

      // Requirement 1: Auto-select if clinic has only 1 active vet
      if (vetsRes.data.length === 1) {
        setFormData((prev) =>
          prev.veterinario_id ? prev : { ...prev, veterinario_id: vetsRes.data[0].id }
        );
      }
      
      // Set configuration from clinic
      const config = configRes.data;
      setFormaCobro(config.forma_cobro || 'cotizacion');
      setGruposPermitidos(config.grupos_permitidos || []);
      setMostrarCultivos(config.mostrar_cultivos !== false);
      
      console.log('Grupos loaded:', gruposRes.data.length, 'Config:', config);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/clinica/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Filter grupos based on clinic configuration
  const filteredGrupos = gruposPermitidos.length > 0
    ? grupos.filter(g => gruposPermitidos.includes(g.id))
    : grupos;

  // Map grupo_id -> area_diagnostica_id for fast lookup (counters + area filter)
  const grupoToArea = React.useMemo(() => {
    const m = {};
    grupos.forEach(g => { m[g.id] = g.area_diagnostica_id; });
    return m;
  }, [grupos]);

  // Resolve selected species NAME -> ID (from Step 1)
  const selectedEspecieId = React.useMemo(() => {
    if (!formData.patient_info.especie) return null;
    if (showOtraEspecie) {
      // User typed free text → match the "Otra especie" catalog entry
      const otra = especiesCatalogo.find(e => e.nombre === 'Otra especie');
      return otra ? otra.id : null;
    }
    const esp = especiesCatalogo.find(e => e.nombre === formData.patient_info.especie);
    return esp ? esp.id : null;
  }, [formData.patient_info.especie, showOtraEspecie, especiesCatalogo]);

  // Base filter: species (strict) + clinic allowed groups + search
  const matchesClinicScope = React.useCallback((p) => {
    if (gruposPermitidos.length > 0 && !gruposPermitidos.includes(p.grupo_id)) return false;
    return true;
  }, [gruposPermitidos]);

  const matchesEspecie = React.useCallback((p) => {
    if (!selectedEspecieId) return true; // no species yet → show all
    return (p.especies || []).includes(selectedEspecieId);
  }, [selectedEspecieId]);

  const matchesSearchForm = React.useCallback((p) => {
    if (!searchPrueba || searchPrueba.length < 2) return true;
    const terms = normalize(searchPrueba).split(/\s+/).filter(Boolean);
    const haystack = `${normalize(p.nombre)} ${normalize(p.codigo)} ${normalize(p.descripcion || '')}`;
    return terms.every(t => haystack.includes(t));
  }, [searchPrueba]);

  const matchesArea = React.useCallback((p) => {
    if (selectedAreas.length === 0) return true;
    return selectedAreas.includes(grupoToArea[p.grupo_id]);
  }, [selectedAreas, grupoToArea]);

  const matchesApp = React.useCallback((p) => {
    if (selectedApps.length === 0) return true;
    return selectedApps.some(id => (p.aplicaciones_clinicas || []).includes(id));
  }, [selectedApps]);

  // Pruebas visible in Step 2 = clinic scope + species + area + app (search applies only to blue box)
  const visiblePruebas = React.useMemo(() => {
    return pruebas.filter(p =>
      matchesClinicScope(p) && matchesEspecie(p) && matchesArea(p) && matchesApp(p)
    );
  }, [pruebas, matchesClinicScope, matchesEspecie, matchesArea, matchesApp]);

  // Dynamic counters (exclude the section's own filter when computing its counts)
  const { areaCounts, appCounts } = React.useMemo(() => {
    const aC = {}, pC = {};
    // For Area counters: apply clinic+especie+app, skip area
    pruebas.forEach(p => {
      if (!matchesClinicScope(p) || !matchesEspecie(p) || !matchesApp(p)) return;
      const aId = grupoToArea[p.grupo_id];
      if (aId) aC[aId] = (aC[aId] || 0) + 1;
    });
    // For App counters: apply clinic+especie+area, skip app
    pruebas.forEach(p => {
      if (!matchesClinicScope(p) || !matchesEspecie(p) || !matchesArea(p)) return;
      (p.aplicaciones_clinicas || []).forEach(id => { pC[id] = (pC[id] || 0) + 1; });
    });
    return { areaCounts: aC, appCounts: pC };
  }, [pruebas, grupoToArea, matchesClinicScope, matchesEspecie, matchesArea, matchesApp]);

  const toggleArea = (id) => setSelectedAreas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleApp = (id) => setSelectedApps(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const clearFormFilters = () => { setSelectedAreas([]); setSelectedApps([]); };
  const activeFormFilterCount = selectedAreas.length + selectedApps.length;

  // Lookup maps
  const areasMap = React.useMemo(() => Object.fromEntries(areasDiagnosticas.map(a => [a.id, a])), [areasDiagnosticas]);
  const appsMap = React.useMemo(() => Object.fromEntries(aplicacionesClinicas.map(a => [a.id, a])), [aplicacionesClinicas]);

  // Visible apps in popover (filtered by internal search, accent-insensitive)
  const visibleAppsInPopover = React.useMemo(() => {
    if (!appInternalSearch.trim()) return aplicacionesClinicas;
    const t = normalize(appInternalSearch);
    return aplicacionesClinicas.filter(a => normalize(a.nombre).includes(t));
  }, [aplicacionesClinicas, appInternalSearch]);

  const handlePruebaToggle = (pruebaId) => {
    setSelectedPruebas((prev) =>
      prev.includes(pruebaId)
        ? prev.filter((id) => id !== pruebaId)
        : [...prev, pruebaId]
    );
  };

  const updatePatientInfo = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      patient_info: { ...prev.patient_info, [field]: value },
    }));
  };

  const updateClinicalData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      clinical_data: { ...prev.clinical_data, [field]: value },
    }));
  };

  const updateCultivoData = (section, values) => {
    setFormData((prev) => ({
      ...prev,
      cultivo_data: { ...prev.cultivo_data, [section]: values },
    }));
  };

  const toggleCultivoCheckbox = (section, value) => {
    const current = formData.cultivo_data[section] || [];
    const newValues = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    updateCultivoData(section, newValues);
  };

  const validateStep = (step) => {
    if (step === 1) {
      if (!formData.patient_info.nombre) {
        toast.error('El nombre del paciente es requerido');
        return false;
      }
      if (!formData.patient_info.especie) {
        toast.error('La especie es requerida');
        return false;
      }
      if (!formData.veterinario_id) {
        toast.error('Debe seleccionar un médico veterinario');
        return false;
      }
    }
    if (step === 2) {
      if (selectedPruebas.length === 0) {
        toast.error('Debe seleccionar al menos una prueba');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  // Calculate total from selected pruebas
  const getSelectedPruebasDetails = () => {
    return pruebas.filter(p => selectedPruebas.includes(p.id));
  };

  // Calculate price considering escalated pricing per group
  const calculatePriceBreakdown = () => {
    const selectedDetails = getSelectedPruebasDetails();
    
    // Group selected pruebas by grupo_id
    const pruebasPorGrupo = {};
    selectedDetails.forEach(prueba => {
      const grupoId = prueba.grupo_id;
      if (!pruebasPorGrupo[grupoId]) {
        pruebasPorGrupo[grupoId] = [];
      }
      pruebasPorGrupo[grupoId].push(prueba);
    });
    
    let total = 0;
    const breakdown = [];
    
    Object.entries(pruebasPorGrupo).forEach(([grupoId, grupoPruebas]) => {
      const grupo = filteredGrupos.find(g => g.id === grupoId) || grupos.find(g => g.id === grupoId);
      
      if (grupo?.usa_precio_escalonado) {
        // Escalated pricing
        const n = grupoPruebas.length;
        const precioBase = grupo.precio_base || 0;
        const incremento = grupo.incremento_adicional || 0;
        const subtotalGrupo = precioBase + (incremento * (n - 1));
        
        breakdown.push({
          tipo: 'escalonado',
          grupoId,
          grupoNombre: grupo.nombre,
          cantidad: n,
          precioBase,
          incremento,
          subtotal: subtotalGrupo,
          pruebas: grupoPruebas,
          formula: n > 1 ? `$${precioBase.toFixed(2)} + ${n-1} × $${incremento.toFixed(2)}` : `$${precioBase.toFixed(2)}`
        });
        
        total += subtotalGrupo;
      } else {
        // Individual pricing
        grupoPruebas.forEach(prueba => {
          const precio = prueba.precio || 0;
          breakdown.push({
            tipo: 'individual',
            prueba,
            precio
          });
          total += precio;
        });
      }
    });
    
    return { total, breakdown };
  };

  const calculateTotal = () => {
    return calculatePriceBreakdown().total;
  };
  
  // Get price display for a specific prueba in a grupo
  const getPriceDisplay = (prueba, grupoId) => {
    const grupo = filteredGrupos.find(g => g.id === grupoId) || grupos.find(g => g.id === grupoId);
    
    if (!grupo?.usa_precio_escalonado) {
      return { display: `$${(prueba.precio || 0).toFixed(2)}`, tipo: 'normal' };
    }
    
    // For escalated groups, show position-based price
    const grupoPruebas = pruebas.filter(p => p.grupo_id === grupoId);
    const selectedInGrupo = grupoPruebas.filter(p => selectedPruebas.includes(p.id));
    const isSelected = selectedPruebas.includes(prueba.id);
    
    if (!isSelected) {
      // Show what price would be if selected
      const nextPosition = selectedInGrupo.length + 1;
      if (nextPosition === 1) {
        return { display: `$${(grupo.precio_base || 0).toFixed(2)}`, tipo: 'base' };
      } else {
        return { display: `+$${(grupo.incremento_adicional || 0).toFixed(2)}`, tipo: 'incremento' };
      }
    }
    
    // Show current position price
    const position = selectedInGrupo.findIndex(p => p.id === prueba.id) + 1;
    if (position === 1) {
      return { display: `$${(grupo.precio_base || 0).toFixed(2)}`, tipo: 'base' };
    } else {
      return { display: `+$${(grupo.incremento_adicional || 0).toFixed(2)}`, tipo: 'incremento' };
    }
  };

  // Handle payment with Stripe
  const handleStripePayment = async (solicitudId) => {
    setIsProcessingPayment(true);
    try {
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        {
          solicitud_id: solicitudId,
          origin_url: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Error al procesar el pago: ' + (error.response?.data?.detail || error.message));
      setIsProcessingPayment(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(1) || !validateStep(2)) return;

    setIsSubmitting(true);

    try {
      const selectedVet = veterinarios.find((v) => v.id === formData.veterinario_id);

      const submitData = {
        ...formData,
        vet_info: {
          nombre: selectedVet?.nombre || clinicaData?.nombre || '',
          clinica: clinicaData?.nombre || '',
          telefono: clinicaData?.telefono || '',
          email: clinicaData?.email || '',
        },
        clinica_id: clinicaData?.id,
        pruebas_ids: selectedPruebas,
      };

      if (formaCobro === 'stripe' && calculateTotal() > 0) {
        // NEW FLOW: Create Stripe checkout WITHOUT creating solicitud first
        // Solicitud will be created only after successful payment
        toast.info('Redirigiendo al pago...');
        setIsProcessingPayment(true);
        
        const response = await axios.post(
          `${API}/payments/create-stripe-checkout`,
          {
            origin_url: window.location.origin,
            form_data: submitData,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        // Cotización/Crédito - create solicitud directly
        const response = await axios.post(
          `${API}/solicitudes`, 
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('¡Solicitud enviada exitosamente!');
        navigate(`/clinica/confirmacion/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Check if it's a credit limit error
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && errorDetail.code === 'CREDIT_LIMIT_EXCEEDED') {
        setCreditLimitError(errorDetail);
        setShowCreditLimitModal(true);
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : 'Error al enviar la solicitud');
      }
      
      setIsSubmitting(false);
      setIsProcessingPayment(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#505BB3]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header - Identical to Dashboard */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src="/amplicare-logo.svg" alt="Amplicare Lab" className="h-9" />
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/clinica/dashboard')}
                variant="outline"
                className="h-10 border-slate-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al Panel
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Compact Title + Progress Steps */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Title */}
            <div>
              <h1 className="text-lg font-bold text-slate-900">
                Solicitud de Pruebas de Laboratorio
              </h1>
              <p className="text-sm text-slate-500">Complete el formulario para enviar su solicitud</p>
            </div>
            
            {/* Compact Steps */}
            <div className="flex items-center gap-2">
              {STEPS.map((step, index) => {
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;
                
                return (
                  <React.Fragment key={step.id}>
                    <div className="flex items-center gap-1.5">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          isCompleted
                            ? 'bg-green-500 text-white'
                            : isActive
                            ? 'bg-[#505BB3] text-white'
                            : 'bg-slate-200 text-slate-500'
                        }`}
                      >
                        {isCompleted ? <Check className="w-3.5 h-3.5" /> : step.id}
                      </div>
                      <span
                        className={`text-sm font-medium hidden md:inline ${
                          isActive ? 'text-[#505BB3]' : isCompleted ? 'text-green-600' : 'text-slate-400'
                        }`}
                      >
                        {step.title}
                      </span>
                    </div>
                    {index < STEPS.length - 1 && (
                      <div
                        className={`w-8 h-0.5 ${
                          currentStep > step.id ? 'bg-green-500' : 'bg-slate-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Datos del Paciente */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* Datos de la Clínica */}
            <FormCard>
              <SectionHeader icon={Building2} title="Datos de la Clínica" />
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <span className="text-xs text-slate-500 block">Clínica</span>
                    <span className="font-semibold text-slate-900">{clinicaData?.nombre}</span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <span className="text-xs text-slate-500 block">Email</span>
                    <span className="font-semibold text-slate-900">{clinicaData?.email}</span>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <span className="text-xs text-slate-500 block">Teléfono</span>
                    <span className="font-semibold text-slate-900">{clinicaData?.telefono}</span>
                  </div>
                </div>

                {veterinarios.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700">
                      <Stethoscope className="w-4 h-4 inline mr-1" />
                      Médico Veterinario
                    </Label>
                    <Select
                      value={formData.veterinario_id}
                      onValueChange={(value) =>
                        setFormData((prev) => ({ ...prev, veterinario_id: value }))
                      }
                    >
                      <SelectTrigger className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]">
                        <SelectValue placeholder="Seleccione un veterinario" />
                      </SelectTrigger>
                      <SelectContent>
                        {veterinarios.map((vet) => (
                          <SelectItem key={vet.id} value={vet.id}>
                            {vet.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </FormCard>

            {/* Fecha y Hora */}
            <FormCard>
              <SectionHeader icon={Calendar} title="Fecha y Hora de la Toma"  />
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Fecha *
                    </Label>
                    <div className="relative mt-1.5">
                      <Input
                        type="date"
                        value={formData.fecha}
                        onChange={(e) => setFormData((prev) => ({ ...prev, fecha: e.target.value }))}
                        className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Hora de Toma *
                    </Label>
                    <div className="relative mt-1.5">
                      <Input
                        type="time"
                        value={formData.hora_toma}
                        onChange={(e) => setFormData((prev) => ({ ...prev, hora_toma: e.target.value }))}
                        className="h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </FormCard>

            {/* Datos del Paciente */}
            <FormCard>
              <SectionHeader icon={PawPrint} title="Datos del Paciente"  />
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Nombre del Paciente *</Label>
                    <Input
                      value={formData.patient_info.nombre}
                      onChange={(e) => updatePatientInfo('nombre', e.target.value)}
                      placeholder="Nombre de la mascota"
                      className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Especie *</Label>
                    <Select
                      value={showOtraEspecie ? '__otra__' : formData.patient_info.especie}
                      onValueChange={(value) => {
                        if (value === '__otra__') {
                          setShowOtraEspecie(true);
                          updatePatientInfo('especie', '');
                        } else {
                          setShowOtraEspecie(false);
                          updatePatientInfo('especie', value);
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]">
                        <SelectValue placeholder="Seleccione la especie" />
                      </SelectTrigger>
                      <SelectContent>
                        {especiesCatalogo.filter(e => e.nombre !== 'Otra especie').map(esp => (
                          <SelectItem key={esp.id} value={esp.nombre}>{esp.nombre}</SelectItem>
                        ))}
                        {especiesCatalogo.some(e => e.nombre === 'Otra especie') && (
                          <SelectItem value="__otra__">Otra especie...</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {showOtraEspecie && (
                      <Input
                        value={formData.patient_info.especie}
                        onChange={(e) => updatePatientInfo('especie', e.target.value)}
                        placeholder="Escriba la especie..."
                        className="mt-2 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                        autoFocus
                        data-testid="input-otra-especie"
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Raza</Label>
                    <Input
                      value={formData.patient_info.raza}
                      onChange={(e) => updatePatientInfo('raza', e.target.value)}
                      placeholder="Raza del paciente"
                      className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Edad</Label>
                    <Input
                      value={formData.patient_info.edad}
                      onChange={(e) => updatePatientInfo('edad', e.target.value)}
                      placeholder="Ej: 3 años"
                      className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Sexo</Label>
                    <Select
                      value={formData.patient_info.sexo}
                      onValueChange={(value) => updatePatientInfo('sexo', value)}
                    >
                      <SelectTrigger className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]">
                        <SelectValue placeholder="Seleccione el sexo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Macho">Macho</SelectItem>
                        <SelectItem value="Hembra">Hembra</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Propietario</Label>
                    <Input
                      value={formData.patient_info.propietario}
                      onChange={(e) => updatePatientInfo('propietario', e.target.value)}
                      placeholder="Nombre del propietario"
                      className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                    />
                  </div>
                </div>
              </div>
            </FormCard>
          </div>
        )}

        {/* Step 2: Pruebas Solicitadas */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <FormCard>
              <SectionHeader icon={FlaskConical} title="Pruebas Solicitadas" />
              <div className="p-5">
                <p className="text-sm text-slate-600 mb-4">
                  Seleccione una o más pruebas del catálogo. Las pruebas están organizadas por categorías.
                </p>

                {/* Buscador Global de Pruebas */}
                <div className="mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar prueba en todas las categorías..."
                      value={searchPrueba}
                      onChange={(e) => setSearchPrueba(e.target.value)}
                      className="pl-10 pr-10"
                    />
                    {searchPrueba && (
                      <button
                        onClick={() => setSearchPrueba('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Filtros Inline: Área Diagnóstica (tabs) + Aplicación Clínica (popover) */}
                {areasDiagnosticas.length > 0 && (
                  <div className="mb-3 flex items-center gap-2">
                    <div className="flex-1 min-w-0 -mx-1 px-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                      <div className="flex items-center gap-1.5 w-max">
                        <button
                          type="button"
                          onClick={() => setSelectedAreas([])}
                          className={`shrink-0 px-3.5 py-1.5 text-xs font-medium rounded-full border transition-all ${
                            selectedAreas.length === 0
                              ? 'bg-slate-900 text-white border-transparent'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900'
                          }`}
                          data-testid="form-area-all"
                        >
                          <Folder className="w-3 h-3 inline mr-1" />
                          Todas
                        </button>
                        {areasDiagnosticas.map(area => {
                          const isSelected = selectedAreas.includes(area.id);
                          const count = areaCounts[area.id] || 0;
                          const disabled = count === 0 && !isSelected;
                          return (
                            <button
                              key={area.id}
                              type="button"
                              onClick={() => toggleArea(area.id)}
                              disabled={disabled}
                              className={`shrink-0 px-3.5 py-1.5 text-xs font-medium rounded-full border transition-all ${
                                disabled
                                  ? 'opacity-40 cursor-not-allowed border-slate-200 text-slate-400 bg-white'
                                  : isSelected
                                    ? 'bg-slate-900 text-white border-transparent'
                                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400 hover:text-slate-900'
                              }`}
                              data-testid={`form-area-${area.id}`}
                            >
                              {area.nombre}
                              <span className={`ml-1.5 text-[10px] ${isSelected ? 'text-white/70' : 'text-slate-400'}`}>
                                {count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <Popover open={appsPopoverOpen} onOpenChange={setAppsPopoverOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className={`shrink-0 flex items-center gap-1.5 px-3 h-9 rounded-full border text-xs font-medium transition-all ${
                            selectedApps.length > 0
                              ? 'bg-[#505BB3]/5 text-[#505BB3] border-[#505BB3]/30'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                          }`}
                          data-testid="form-apps-toggle"
                        >
                          <SlidersHorizontal className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Aplicación Clínica</span>
                          <span className="sm:hidden">Apps</span>
                          {selectedApps.length > 0 && (
                            <span className="flex items-center justify-center min-w-[18px] h-4 px-1 bg-[#505BB3] text-white text-[10px] font-bold rounded-full">
                              {selectedApps.length}
                            </span>
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[calc(100vw-2rem)] sm:w-96 max-h-[70vh] p-0" align="end" sideOffset={8}>
                        <div className="p-3">
                          <div className="relative mb-2">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                            <input
                              type="text"
                              value={appInternalSearch}
                              onChange={(e) => setAppInternalSearch(e.target.value)}
                              placeholder="Buscar aplicación..."
                              className="w-full h-8 pl-8 pr-7 text-xs rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#505BB3]/30 focus:border-[#505BB3]"
                              data-testid="form-apps-search"
                            />
                            {appInternalSearch && (
                              <button
                                type="button"
                                onClick={() => setAppInternalSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          <div className="max-h-72 overflow-y-auto pr-1 -mr-1 space-y-0.5">
                            {visibleAppsInPopover.length === 0 ? (
                              <div className="text-xs text-slate-400 px-1 py-2 text-center">
                                Sin coincidencias
                              </div>
                            ) : (
                              visibleAppsInPopover.map(app => {
                                const isSel = selectedApps.includes(app.id);
                                const count = appCounts[app.id] || 0;
                                const disabled = count === 0 && !isSel;
                                return (
                                  <button
                                    key={app.id}
                                    type="button"
                                    onClick={() => toggleApp(app.id)}
                                    disabled={disabled}
                                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors ${
                                      disabled
                                        ? 'opacity-40 cursor-not-allowed'
                                        : isSel
                                          ? 'bg-[#505BB3]/5'
                                          : 'hover:bg-slate-50'
                                    }`}
                                    data-testid={`form-app-${app.id}`}
                                  >
                                    <span
                                      aria-hidden="true"
                                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                                        isSel ? 'border-transparent bg-[#505BB3]' : 'border-slate-300 bg-white'
                                      }`}
                                    >
                                      {isSel && (
                                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                          <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                      )}
                                    </span>
                                    <span className={`text-xs flex-1 truncate ${isSel ? 'text-slate-900 font-medium' : 'text-slate-700'}`}>
                                      {app.nombre}
                                    </span>
                                    <span className={`text-[10px] tabular-nums ${count === 0 ? 'text-slate-300' : 'text-slate-400'}`}>
                                      {count}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-100 bg-slate-50/60">
                          <button
                            type="button"
                            onClick={() => setSelectedApps([])}
                            disabled={selectedApps.length === 0}
                            className="text-xs font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Limpiar
                          </button>
                          <button
                            type="button"
                            onClick={() => setAppsPopoverOpen(false)}
                            className="px-4 py-1.5 text-xs font-semibold text-white bg-[#505BB3] rounded-full hover:bg-[#434ea0] transition-colors"
                          >
                            Aplicar
                          </button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {/* Active filter chips */}
                {activeFormFilterCount > 0 && (
                  <div className="mb-4 flex items-center flex-wrap gap-1.5">
                    <span className="text-[11px] text-slate-500 mr-1">Filtros:</span>
                    {selectedAreas.map(id => {
                      const a = areasMap[id];
                      return a ? (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleArea(id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-medium rounded-full hover:bg-slate-200 transition-colors"
                        >
                          {a.nombre}
                          <X className="w-3 h-3" />
                        </button>
                      ) : null;
                    })}
                    {selectedApps.map(id => {
                      const a = appsMap[id];
                      return a ? (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleApp(id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#505BB3]/10 text-[#505BB3] border border-[#505BB3]/20 text-[11px] font-medium rounded-full hover:bg-[#505BB3]/15 transition-colors"
                        >
                          {a.nombre}
                          <X className="w-3 h-3" />
                        </button>
                      ) : null;
                    })}
                    <button
                      type="button"
                      onClick={clearFormFilters}
                      className="text-[11px] text-[#505BB3] font-medium hover:underline ml-1"
                    >
                      Limpiar todo
                    </button>
                  </div>
                )}

                {/* Especie info banner */}
                {formData.patient_info.especie && selectedEspecieId && (
                  <div className="mb-3 flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                    <PawPrint className="w-3.5 h-3.5 shrink-0" />
                    <span>Mostrando pruebas disponibles para <strong>{formData.patient_info.especie}</strong></span>
                  </div>
                )}

                {/* Resultados de búsqueda */}
                {searchPrueba.length >= 2 && (
                  <div className="mb-4 border border-blue-200 rounded-lg bg-blue-50 p-4">
                    <p className="text-sm font-medium text-blue-800 mb-3">
                      <Search className="w-4 h-4 inline mr-2" />
                      Resultados para "{searchPrueba}":
                    </p>
                    {(() => {
                      // Search respects: clinic scope + species + area + app filters + accent-insensitive
                      const resultados = visiblePruebas.filter(matchesSearchForm);
                      if (resultados.length === 0) {
                        return (
                          <p className="text-sm text-slate-500 italic">
                            No se encontraron pruebas que coincidan con "{searchPrueba}"
                          </p>
                        );
                      }
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                          {resultados.map((prueba) => {
                            const grupo = grupos.find(g => g.id === prueba.grupo_id);
                            const isSelected = selectedPruebas.includes(prueba.id);
                            const priceInfo = getPriceDisplay(prueba, prueba.grupo_id);
                            return (
                              <label
                                key={prueba.id}
                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                  isSelected
                                    ? 'bg-[#505BB3]/10 border-[#505BB3]'
                                    : 'bg-white border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => handlePruebaToggle(prueba.id)}
                                  className="mt-0.5 data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <span className="text-sm font-medium text-slate-900">
                                      {prueba.nombre}
                                    </span>
                                    <span className={`text-sm font-bold whitespace-nowrap ${priceInfo.color}`}>
                                      {priceInfo.display}
                                    </span>
                                  </div>
                                  <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded mt-1 inline-block">
                                    {grupo?.nombre || 'Sin categoría'}
                                  </span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {filteredGrupos.length === 0 || visiblePruebas.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    {visiblePruebas.length === 0 && selectedEspecieId
                      ? `No hay pruebas disponibles para la especie seleccionada con los filtros actuales.`
                      : 'No hay pruebas disponibles en el catálogo'}
                  </div>
                ) : (
                  <Accordion type="multiple" className="space-y-3">
                    {filteredGrupos.map((grupo, index) => {
                      const grupoPruebas = visiblePruebas.filter((p) => p.grupo_id === grupo.id);
                      if (grupoPruebas.length === 0) return null;
                      const selectedInGrupo = grupoPruebas.filter((p) =>
                        selectedPruebas.includes(p.id)
                      );
                      const selectedCount = selectedInGrupo.length;
                      const colors = ['red', 'purple', 'green', 'blue', 'orange', 'yellow', 'pink'];
                      const color = colors[index % colors.length];
                      
                      // Calculate subtotal for escalated groups
                      let grupoSubtotal = 0;
                      if (grupo.usa_precio_escalonado && selectedCount > 0) {
                        grupoSubtotal = (grupo.precio_base || 0) + ((grupo.incremento_adicional || 0) * (selectedCount - 1));
                      }

                      return (
                        <AccordionItem 
                          key={grupo.id} 
                          value={grupo.id} 
                          className="border border-slate-200 rounded-lg overflow-hidden"
                        >
                          <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3 w-full pr-4">
                              <CategoryIndicator color={color} />
                              <div className="flex-1 text-left">
                                <span className="font-medium text-slate-900">
                                  {grupo.nombre}
                                </span>
                                {grupo.usa_precio_escalonado && (
                                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                    Precio escalonado
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-slate-500 mr-2">
                                {grupoPruebas.length} pruebas
                              </span>
                              {selectedCount > 0 && (
                                <span className="bg-[#505BB3] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                                  {selectedCount}
                                </span>
                              )}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4 pt-2 bg-slate-50">
                            {/* Show escalated pricing info */}
                            {grupo.usa_precio_escalonado && (
                              <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                                  <span className="text-green-600">💡</span>
                                  Precio escalonado: ${(grupo.precio_base || 0).toFixed(2)} la primera + ${(grupo.incremento_adicional || 0).toFixed(2)} cada adicional
                                </p>
                                {selectedCount > 0 && (
                                  <p className="text-sm text-green-700 mt-1 font-semibold">
                                    📦 {selectedCount} prueba{selectedCount > 1 ? 's' : ''} = ${grupoSubtotal.toFixed(2)}
                                  </p>
                                )}
                              </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {grupoPruebas.map((prueba) => {
                                const priceInfo = getPriceDisplay(prueba, grupo.id);
                                const isSelected = selectedPruebas.includes(prueba.id);
                                
                                return (
                                <label
                                  key={prueba.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                    isSelected
                                      ? 'bg-[#505BB3]/5 border-[#505BB3]'
                                      : 'bg-white border-slate-200 hover:border-slate-300'
                                  }`}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => handlePruebaToggle(prueba.id)}
                                    className="mt-0.5 data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <span className="text-sm font-medium text-slate-900">
                                        {prueba.nombre}
                                      </span>
                                      <span className={`text-sm font-bold whitespace-nowrap ${
                                        grupo.usa_precio_escalonado 
                                          ? (priceInfo.tipo === 'base' ? 'text-green-600' : 'text-green-500')
                                          : 'text-green-600'
                                      }`}>
                                        {priceInfo.display}
                                      </span>
                                    </div>
                                    <span className="text-xs text-slate-500">
                                      Código: {prueba.codigo}
                                    </span>
                                    {prueba.descripcion && (
                                      <span className="text-xs text-slate-400 block mt-0.5">
                                        {prueba.descripcion}
                                      </span>
                                    )}
                                  </div>
                                </label>
                              );
                              })}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}

                {selectedPruebas.length > 0 && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">
                          {selectedPruebas.length} prueba(s) seleccionada(s)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="text-lg font-bold text-green-700">
                          Total: ${calculateTotal().toFixed(2)} MXN
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </FormCard>
          </div>
        )}

        {/* Step 3: Información Clínica */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Observaciones - Primera posición */}
            <FormCard>
              <SectionHeader icon={ClipboardList} title="Observaciones"  />
              <div className="p-5">
                <Label className="text-sm font-medium text-slate-700">
                  Diagnóstico presuntivo, historia clínica / Tratamiento antes de la toma de muestra, etc.
                </Label>
                <Textarea
                  value={formData.observaciones}
                  onChange={(e) => setFormData((prev) => ({ ...prev, observaciones: e.target.value }))}
                  placeholder="Escriba aquí cualquier información relevante para el análisis..."
                  rows={4}
                  className="mt-1.5 border-slate-300 focus:border-[#505BB3] focus:ring-[#505BB3]"
                />
              </div>
            </FormCard>

            {/* Datos Generales */}
            <FormCard>
              <SectionHeader icon={ClipboardList} title="Datos Generales" />
              <div className="p-5 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-slate-700">
                      ¿Toma antibióticos actualmente?
                    </Label>
                    <RadioGroup
                      value={formData.clinical_data.toma_antibioticos.toString()}
                      onValueChange={(value) => updateClinicalData('toma_antibioticos', value === 'true')}
                      className="flex gap-6"
                    >
                      <label className="flex items-center gap-2 cursor-pointer">
                        <RadioGroupItem value="true" className="text-[#505BB3]" />
                        <span className="text-sm">Sí</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <RadioGroupItem value="false" className="text-[#505BB3]" />
                        <span className="text-sm">No</span>
                      </label>
                    </RadioGroup>

                    {formData.clinical_data.toma_antibioticos && (
                      <div className="pl-4 border-l-2 border-[#505BB3]">
                        <Label className="text-sm font-medium text-slate-700">Fecha de última dosis</Label>
                        <Input
                          type="date"
                          value={formData.clinical_data.fecha_ultima_dosis || ''}
                          onChange={(e) => updateClinicalData('fecha_ultima_dosis', e.target.value)}
                          className="mt-1.5 h-10 border-slate-300 focus:border-[#505BB3]"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-slate-700">
                      ¿Alérgico a antibióticos?
                    </Label>
                    <RadioGroup
                      value={formData.clinical_data.alergico_antibioticos.toString()}
                      onValueChange={(value) => updateClinicalData('alergico_antibioticos', value === 'true')}
                      className="flex gap-6"
                    >
                      <label className="flex items-center gap-2 cursor-pointer">
                        <RadioGroupItem value="true" className="text-[#505BB3]" />
                        <span className="text-sm">Sí</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <RadioGroupItem value="false" className="text-[#505BB3]" />
                        <span className="text-sm">No</span>
                      </label>
                    </RadioGroup>

                    {formData.clinical_data.alergico_antibioticos && (
                      <div className="pl-4 border-l-2 border-[#505BB3]">
                        <Label className="text-sm font-medium text-slate-700">¿Cuál?</Label>
                        <Input
                          value={formData.clinical_data.cual_antibiotico || ''}
                          onChange={(e) => updateClinicalData('cual_antibiotico', e.target.value)}
                          placeholder="Especifique el antibiótico"
                          className="mt-1.5 h-10 border-slate-300 focus:border-[#505BB3]"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Tiempo de Evolución</Label>
                    <Input
                      value={formData.clinical_data.tiempo_evolucion || ''}
                      onChange={(e) => updateClinicalData('tiempo_evolucion', e.target.value)}
                      placeholder="Ej: 3 días, 2 semanas..."
                      className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3]"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Origen de la toma de muestra</Label>
                    <Input
                      value={formData.clinical_data.origen_toma_muestra || ''}
                      onChange={(e) => updateClinicalData('origen_toma_muestra', e.target.value)}
                      placeholder="Ej: Sangre venosa, orina..."
                      className="mt-1.5 h-11 border-slate-300 focus:border-[#505BB3]"
                    />
                  </div>
                </div>
              </div>
            </FormCard>

            {/* Secciones de Cultivo - Condicional según configuración */}
            {mostrarCultivos && (
            <FormCard>
              <SectionHeader icon={FlaskConical} title="Secciones de Cultivo y Microscopía"  />
              <div className="p-5">
                <p className="text-sm text-slate-600 mb-4">
                  Expanda las secciones relevantes y complete la información necesaria.
                </p>
                
                <Accordion type="multiple" className="space-y-3">
                  {/* Faríngea */}
                  <AccordionItem value="faringea" className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <CategoryIndicator color="red" />
                        <span className="font-medium">Cultivo-Microscopía Faríngea/Nasofaríngea</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                        {['Ulceraciones', 'Manchas blanquecinas', 'Tos', 'Amígdalas inflamadas', 'Región amigdalina y faríngea normales'].map((item) => (
                          <label key={item} className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                            <Checkbox
                              checked={(formData.cultivo_data.faringea || []).includes(item)}
                              onCheckedChange={() => toggleCultivoCheckbox('faringea', item)}
                              className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                            />
                            <span className="text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Exudado */}
                  <AccordionItem value="exudado" className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <CategoryIndicator color="purple" />
                        <span className="font-medium">Cultivo-Microscopía Exudado Diversos</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                        {['Exudado purulento', 'Exudado sanguinolento', 'Dolor a la manipulación', 'Fiebre documentada (MVZ)'].map((item) => (
                          <label key={item} className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                            <Checkbox
                              checked={(formData.cultivo_data.exudado_diversos || []).includes(item)}
                              onCheckedChange={() => toggleCultivoCheckbox('exudado_diversos', item)}
                              className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                            />
                            <span className="text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Nasal */}
                  <AccordionItem value="nasal" className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <CategoryIndicator color="green" />
                        <span className="font-medium">Cultivo-Microscopía Nasal</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                        {['Estornudos', 'Secreción o congestión nasal', 'Cuadro respiratorio recurrente', 'Fosas nasales sin alteraciones visibles'].map((item) => (
                          <label key={item} className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                            <Checkbox
                              checked={(formData.cultivo_data.nasal || []).includes(item)}
                              onCheckedChange={() => toggleCultivoCheckbox('nasal', item)}
                              className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                            />
                            <span className="text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Urocultivo */}
                  <AccordionItem value="urocultivo" className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <CategoryIndicator color="blue" />
                        <span className="font-medium">Urocultivo-Microscopía de Sedimento Urinario</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 bg-slate-50 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">¿Antecedente de infección urinaria?</Label>
                          <RadioGroup
                            value={(formData.cultivo_data.urocultivo?.antecedente_infeccion || false).toString()}
                            onValueChange={(value) =>
                              updateCultivoData('urocultivo', {
                                ...formData.cultivo_data.urocultivo,
                                antecedente_infeccion: value === 'true',
                              })
                            }
                            className="flex gap-4"
                          >
                            <label className="flex items-center gap-2 cursor-pointer">
                              <RadioGroupItem value="true" />
                              <span className="text-sm">Sí</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <RadioGroupItem value="false" />
                              <span className="text-sm">No</span>
                            </label>
                          </RadioGroup>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">¿Olor fuerte o fétido en orina?</Label>
                          <RadioGroup
                            value={(formData.cultivo_data.urocultivo?.olor_fuerte || false).toString()}
                            onValueChange={(value) =>
                              updateCultivoData('urocultivo', {
                                ...formData.cultivo_data.urocultivo,
                                olor_fuerte: value === 'true',
                              })
                            }
                            className="flex gap-4"
                          >
                            <label className="flex items-center gap-2 cursor-pointer">
                              <RadioGroupItem value="true" />
                              <span className="text-sm">Sí</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <RadioGroupItem value="false" />
                              <span className="text-sm">No</span>
                            </label>
                          </RadioGroup>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {[
                          'Signos compatibles con dolor abdominal o lumbar',
                          'Dolor abdominal a la palpación (MVZ)',
                          'Esfuerzo o dolor aparente al orinar',
                          'Micciones frecuentes con poco volumen',
                          'Secreción uretral o vaginal',
                          'Orina turbia o sangre visible',
                        ].map((item) => (
                          <label key={item} className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                            <Checkbox
                              checked={(formData.cultivo_data.urocultivo?.sintomas || []).includes(item)}
                              onCheckedChange={() => {
                                const current = formData.cultivo_data.urocultivo?.sintomas || [];
                                const newValues = current.includes(item)
                                  ? current.filter((v) => v !== item)
                                  : [...current, item];
                                updateCultivoData('urocultivo', {
                                  ...formData.cultivo_data.urocultivo,
                                  sintomas: newValues,
                                });
                              }}
                              className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                            />
                            <span className="text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Hongos */}
                  <AccordionItem value="hongos" className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <CategoryIndicator color="orange" />
                        <span className="font-medium">Cultivo-Microscopía Búsqueda de Hongos</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 bg-slate-50 space-y-4">
                      <div className="pt-2">
                        <Label className="text-sm font-medium">Tipo de lesión observada</Label>
                        <div className="flex gap-4 mt-2">
                          {['Descamativa', 'Exudativa'].map((item) => (
                            <label key={item} className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                              <Checkbox
                                checked={(formData.cultivo_data.hongos_tipo_lesion || []).includes(item)}
                                onCheckedChange={() => toggleCultivoCheckbox('hongos_tipo_lesion', item)}
                                className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                              />
                              <span className="text-sm">{item}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Sitio de lesión</Label>
                        <Input
                          value={formData.cultivo_data.hongos_sitio || ''}
                          onChange={(e) => updateCultivoData('hongos_sitio', e.target.value)}
                          placeholder="Especifique el sitio de la lesión..."
                          className="mt-1.5 h-10 border-slate-300 focus:border-[#505BB3]"
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Coprocultivo */}
                  <AccordionItem value="coprocultivo" className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <CategoryIndicator color="yellow" />
                        <span className="font-medium">Coprocultivo</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 bg-slate-50 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                        {['Diarrea crónica', 'Diarrea aguda/reciente', 'Signos compatibles con dolor abdominal', 'Vómito', 'Letargia/decaimiento', 'Fiebre documentada (MVZ)'].map((item) => (
                          <label key={item} className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                            <Checkbox
                              checked={(formData.cultivo_data.coprocultivo || []).includes(item)}
                              onCheckedChange={() => toggleCultivoCheckbox('coprocultivo', item)}
                              className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                            />
                            <span className="text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Ingesta de alimento/líquido sospechoso ¿Cuál?</Label>
                        <Input
                          value={formData.cultivo_data.coprocultivo_ingesta || ''}
                          onChange={(e) => updateCultivoData('coprocultivo_ingesta', e.target.value)}
                          placeholder="Especifique..."
                          className="mt-1.5 h-10 border-slate-300 focus:border-[#505BB3]"
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Traqueal */}
                  <AccordionItem value="traqueal" className="border border-slate-200 rounded-lg overflow-hidden">
                    <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <CategoryIndicator color="pink" />
                        <span className="font-medium">Cultivo-Microscopía Traqueal/Bronquial</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4 bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
                        {['Tos crónica', 'Tos reciente', 'Dificultad respiratoria (disnea)', 'Respiración rápida (taquipnea)', 'Pérdida de peso', 'Fiebre documentada (MVZ)'].map((item) => (
                          <label key={item} className="flex items-center gap-2 p-2 bg-white rounded border border-slate-200 cursor-pointer hover:bg-slate-50">
                            <Checkbox
                              checked={(formData.cultivo_data.traqueal || []).includes(item)}
                              onCheckedChange={() => toggleCultivoCheckbox('traqueal', item)}
                              className="data-[state=checked]:bg-[#505BB3] data-[state=checked]:border-[#505BB3]"
                            />
                            <span className="text-sm">{item}</span>
                          </label>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </FormCard>
            )}
          </div>
        )}

        {/* Step 4: Resumen y Pago */}
        {currentStep === 4 && (
          <div className="space-y-6">
            {/* Resumen de Pruebas */}
            <FormCard>
              <SectionHeader icon={ShoppingCart} title="Resumen de Pruebas Solicitadas" />
              <div className="p-5">
                <div className="space-y-3">
                  {calculatePriceBreakdown().breakdown.map((item, index) => (
                    item.tipo === 'escalonado' ? (
                      <div 
                        key={`escalonado-${item.grupoId}`}
                        className="p-3 bg-green-50 rounded-lg border border-green-200"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-green-600">📦</span>
                            <span className="font-medium text-slate-900">{item.grupoNombre}</span>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              {item.cantidad} prueba{item.cantidad > 1 ? 's' : ''}
                            </span>
                          </div>
                          <span className="font-bold text-green-600">${item.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="mt-2 pl-6 space-y-1">
                          {item.pruebas.map((p, i) => (
                            <p key={p.id} className="text-sm text-slate-600">• {p.nombre}</p>
                          ))}
                          <p className="text-xs text-green-600 mt-1">
                            Precio escalonado: {item.formula}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div 
                        key={`individual-${item.prueba.id}`}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="flex-1">
                          <span className="font-medium text-slate-900">{item.prueba.nombre}</span>
                          <span className="text-xs text-slate-500 ml-2">({item.prueba.codigo})</span>
                        </div>
                        <span className="font-bold text-green-600">${(item.precio || 0).toFixed(2)}</span>
                      </div>
                    )
                  ))}
                </div>
                
                {/* Total */}
                <div className="mt-6 pt-4 border-t-2 border-slate-300">
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-slate-900">TOTAL A PAGAR:</span>
                    <span className="text-2xl font-bold text-green-600">${calculateTotal().toFixed(2)} MXN</span>
                  </div>
                </div>
              </div>
            </FormCard>

            {/* Datos del Paciente (Resumen) */}
            <FormCard>
              <SectionHeader icon={PawPrint} title="Datos del Paciente" />
              <div className="p-5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-xs text-slate-500">Paciente</span>
                    <p className="font-medium text-slate-900">{formData.patient_info.nombre}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Especie</span>
                    <p className="font-medium text-slate-900">{formData.patient_info.especie}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Raza</span>
                    <p className="font-medium text-slate-900">{formData.patient_info.raza || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Edad</span>
                    <p className="font-medium text-slate-900">{formData.patient_info.edad || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Sexo</span>
                    <p className="font-medium text-slate-900">{formData.patient_info.sexo || '-'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Propietario</span>
                    <p className="font-medium text-slate-900">{formData.patient_info.propietario || '-'}</p>
                  </div>
                </div>
              </div>
            </FormCard>

            {/* Forma de Pago */}
            <FormCard>
              <SectionHeader 
                icon={CreditCard} 
                title="Pago" 
              />
              <div className="p-5">
                {formaCobro === 'stripe' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <CreditCard className="w-6 h-6 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-blue-900">Pago con Tarjeta</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Al hacer clic en "Pagar y Enviar", será redirigido a la página segura de Stripe 
                          para completar el pago con tarjeta de crédito o débito.
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons@6.6.6/flags/4x3/us.svg" alt="Visa" className="h-4" />
                          <span className="text-xs text-blue-600">Visa, Mastercard, American Express</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <FileText className="w-6 h-6 text-amber-600 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-amber-900">Crédito habilitado</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          Tu clínica cuenta con crédito activo. El laboratorio procesará la solicitud 
                          y el pago se realizará posteriormente según los términos acordados.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </FormCard>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={currentStep === 1 ? () => navigate('/clinica/dashboard') : prevStep}
            className="h-12 px-6 border-slate-300 hover:bg-slate-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {currentStep === 1 ? 'Cancelar' : 'Anterior'}
          </Button>

          {currentStep < 4 ? (
            <Button 
              onClick={nextStep} 
              className="h-12 px-8 bg-[#505BB3] hover:bg-[#404A9C] text-white"
            >
              Siguiente
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || isProcessingPayment}
              className={`h-12 px-8 text-white ${
                formaCobro === 'stripe' 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {isSubmitting || isProcessingPayment ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isProcessingPayment ? 'Procesando pago...' : 'Enviando...'}
                </>
              ) : formaCobro === 'stripe' ? (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pagar y Enviar (${calculateTotal().toFixed(2)})
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Solicitud
                </>
              )}
            </Button>
          )}
        </div>
      </main>

      {/* Credit Limit Exceeded Modal */}
      <Dialog open={showCreditLimitModal} onOpenChange={setShowCreditLimitModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Límite de Crédito Excedido
            </DialogTitle>
          </DialogHeader>
          {creditLimitError && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">
                No es posible crear esta solicitud porque excede el límite de crédito de su clínica.
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Saldo actual:</span>
                  <span className="font-medium text-slate-900">
                    ${creditLimitError.saldo_actual?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Límite de crédito:</span>
                  <span className="font-medium text-slate-900">
                    ${creditLimitError.limite_credito?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Monto de esta solicitud:</span>
                  <span className="font-medium text-slate-900">
                    ${creditLimitError.monto_solicitud?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="border-t border-red-200 pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-red-600 font-medium">Excedente:</span>
                    <span className="font-bold text-red-600">
                      ${creditLimitError.excedente?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  <strong>Crédito disponible:</strong> ${creditLimitError.credito_disponible?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-blue-600 mt-2">
                  Para continuar, por favor comuníquese con el laboratorio para realizar un pago o solicitar una ampliación de su línea de crédito.
                </p>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                <Phone className="w-4 h-4 text-[#505BB3]" />
                <span>Contacto: contacto@amplicarelab.com</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setShowCreditLimitModal(false)}
              className="bg-[#505BB3] hover:bg-[#404A9C] text-white"
              data-testid="close-credit-limit-modal"
            >
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
